import {
  AfterContentChecked,
  AfterContentInit,
  AfterViewChecked,
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  QueryList,
  Renderer2,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../molecules/button/button.component';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { TeaserComponent } from '../../molecules/teaser/teaser.component';
import { Teaser } from '../../../shared/interfaces/teaser.interface';
import { MoreLessType } from '../../../shared/enums/more-less-type.enums';
import { CapitalizePipe } from '../../capitalize.pipe';

@Component({
  selector: 'hdi-container-more-less',
  standalone: true,
  imports: [CommonModule, ButtonComponent, TeaserComponent, CapitalizePipe],
  templateUrl: './container-more-less.component.html',
  styleUrls: ['./container-more-less.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class ContainerMoreLessComponent
  implements
    AfterContentInit,
    AfterContentChecked,
    AfterViewInit,
    AfterViewChecked
{
  @Input() injected: boolean = true;
  @Input() columns: {
    mobileSmall: number; // Under 559px
    mobileMedium: number; // 560px - 767px
    tablet: number; // 768px - 989px
    desktopSmall: number; // 990px - 1199px
    desktopMedium: number; // 1200px - 1499px
    desktopLarge: number; // Major than 1500px
  } = {
    mobileSmall: 12,
    mobileMedium: 12,
    tablet: 6,
    desktopSmall: 6,
    desktopMedium: 3,
    desktopLarge: 3,
  };
  @Input() cards?: Teaser[];
  @Input() moreLessType: MoreLessType = MoreLessType.TEASER;
  @Input() isUnrolled: boolean = false;
  @Input({ required: true }) initialVisibleTeasers: number = 0;
  @Input() teaserStep: number = 3;
  @Input() label: string = '';
  @Input() resetUnrolled: (newVisibleTeasers: {
    teasersLength: number;
    visibleTeasers: number;
  }) => void = (newVisibleTeasers: {
    teasersLength: number;
    visibleTeasers: number;
  }) => {
    this.visible_teasers = newVisibleTeasers.visibleTeasers;
    this.teasersLength = newVisibleTeasers.teasersLength;
    this.checkUnrolledState();
  };

  @ContentChildren('teaser', { descendants: true, read: ElementRef })
  teasers!: QueryList<ElementRef>;
  @ViewChildren('cardList')
  cardList!: QueryList<ElementRef>;

  teasersLength: number = 0;
  type: ButtonType = ButtonType.SECONDARY_ICON;
  public readonly MoreLessType = MoreLessType;
  visible_teasers: number;

  get classes() {
    const prefix = 'more-less';
    return `
      ${prefix}-xs-${this.columns.mobileSmall}
      ${prefix}-sm-${this.columns.mobileMedium}
      ${prefix}-md-${this.columns.tablet}
      ${prefix}-lg-${this.columns.desktopSmall}
      ${prefix}-xl-${this.columns.desktopMedium}
      ${prefix}-xxl-${this.columns.desktopLarge}`;
  }

  constructor(private renderer: Renderer2) {
    // Here 0 is assigned
    this.visible_teasers = this.initialVisibleTeasers;
  }

  ngAfterContentInit(): void {
    if (this.injected) {
      this.initializeVisibleTeasers();
    }
  }

  ngAfterViewInit(): void {
    if (!this.injected) {
      this.initializeVisibleTeasers();
    }
  }

  ngAfterContentChecked(): void {
    if (this.injected) {
      this.updateVisibleTeasers();
    }
  }

  ngAfterViewChecked(): void {
    if (!this.injected) {
      this.updateVisibleTeasers();
    }
  }

  get showMoreLessLabel() {
    return this.isUnrolled ? `Weniger ${this.label}` : `Mehr ${this.label}`;
  }

  get iconSelected() {
    return this.isUnrolled ? 'minus' : 'plus';
  }

  initializeVisibleTeasers() {
    this.visible_teasers = this.initialVisibleTeasers;

    if (this.injected) {
      this.teasersLength = this.teasers?.length ?? 0;
      this.teasers.forEach((teaser, index) => {
        if (index >= this.visible_teasers) {
          this.renderer.setStyle(teaser?.nativeElement, 'display', 'none');
        }
      });
    } else {
      this.teasersLength = this.cardList?.length ?? 0;
      this.cardList.forEach((card, index) => {
        if (index >= this.visible_teasers) {
          this.renderer.setStyle(card?.nativeElement, 'display', 'none');
        }
      });
    }
  }

  updateVisibleTeasers() {
    if (this.injected) {
      this.teasers?.forEach((teaser, index) => {
        if (index >= this.visible_teasers) {
          this.renderer.setStyle(teaser?.nativeElement, 'display', 'none');
        } else {
          this.renderer.setStyle(teaser?.nativeElement, 'display', 'block');
        }
      });
    } else {
      this.cardList?.forEach((card, index) => {
        if (index >= this.visible_teasers) {
          this.renderer.setStyle(card?.nativeElement, 'display', 'none');
        } else {
          this.renderer.setStyle(card?.nativeElement, 'display', 'block');
        }
      });
    }
  }

  showMoreLess() {
    if (this.visible_teasers < this.teasersLength) {
      this.visible_teasers += this.teaserStep;
      if (this.injected) {
        this.teasers?.forEach((teaser, index) => {
          if (index < this.visible_teasers) {
            this.renderer.setStyle(teaser?.nativeElement, 'display', 'block');
          }
        });
      } else {
        this.cardList.forEach((teaser, index) => {
          if (index < this.visible_teasers) {
            this.renderer.setStyle(teaser?.nativeElement, 'display', 'block');
          }
        });
      }
      this.checkUnrolledState();
    } else {
      this.visible_teasers = this.initialVisibleTeasers;
      if (this.injected) {
        this.teasers?.forEach((teaser, index) => {
          if (index >= this.visible_teasers) {
            this.renderer.setStyle(teaser?.nativeElement, 'display', 'none');
          }
        });
      } else {
        this.cardList?.forEach((teaser, index) => {
          if (index >= this.visible_teasers) {
            this.renderer.setStyle(teaser?.nativeElement, 'display', 'none');
          }
        });
      }
      this.checkUnrolledState();
    }
  }

  checkUnrolledState() {
    if (this.visible_teasers >= this.teasersLength) {
      this.isUnrolled = true;
    } else {
      this.isUnrolled = false;
    }
  }
}
